import React, { useState, useRef, useCallback } from 'react';
import styled, { css, keyframes, useTheme } from 'styled-components';
import PropTypes from 'prop-types';
import LytTag, { TAG_VARIANTS, TAG_APPEARANCES } from '../tag/LytTag';
import LytIcon from '../icon/LytIcon';
import { ICON_NAMES } from '../icon/icon-constants';
const shake = keyframes`
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
    20%, 40%, 60%, 80% { transform: translateX(2px); }
`;
const MultiInputContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    position: relative;
`;
const InputContainer = styled.div.withConfig({
    shouldForwardProp: (prop) =>
        !['hasSelectedValues', 'hasError', 'isShaking'].includes(prop),
})`
    position: relative;
    display: flex;
    align-items: center;
    min-height: 42px;
    padding: 6px 10px;
    padding-right: ${({ hasSelectedValues }) =>
        hasSelectedValues ? '70px' : '10px'};
    border-radius: 8px;
    border: 2px solid
        ${({ theme, hasError }) =>
            hasError
                ? theme.components.lyt_input.border.error
                : theme.components.lyt_input.border.default};
    background: ${({ theme }) => theme.components.lyt_input.background.default};
    cursor: text;
    flex-wrap: wrap;
    gap: 6px;

    ${({ isShaking }) =>
        isShaking &&
        css`
            animation: ${shake} 0.4s ease-in-out;
        `}

    &:hover {
        background: ${({ theme }) =>
            theme.components.lyt_input.background.hover};
    }

    /* Focus styling - border change to match LytTextField */
    &:focus-within {
        background: ${({ theme, hasError }) =>
            hasError
                ? theme.components.lyt_input.background.error
                : theme.components.lyt_input.background.focus};
        border-color: ${({ theme, hasError }) =>
            hasError
                ? theme.components.lyt_input.border.error
                : theme.components.lyt_input.border.focus};
    }
`;
const TagsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    flex: 1;
`;
const HiddenInput = styled.input`
    border: none;
    background: transparent;
    outline: none;
    flex: 1;
    min-width: 120px;
    color: ${({ theme }) => theme.components.dropdown?.input?.text || '#333'};
    font-size: 14px;

    &::placeholder {
        color: ${({ theme }) =>
            theme.components.dropdown?.input?.placeholder || '#999'};
    }
`;
const IconsContainer = styled.div`
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    gap: 8px;
    pointer-events: all;
`;
const ClearButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${({ theme }) =>
        theme.components.dropdown?.input?.clearButton?.background || '#343436'};
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
        background: ${({ theme }) =>
            theme.components.dropdown?.input?.clearButton?.hover || '#474749'};
    }

    svg {
        width: 12px;
        height: 12px;
    }
`;
const ErrorMessage = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.components.lyt_input.border.error};
`;

/**
 * @description Gets the text to display in the input placeholder
 * @param {array} values - The current values
 * @param {string} placeholder - The placeholder text
 * @returns {string} - The text to display as placeholder
 */
const getPlaceholderText = (values, placeholder) => {
    if (!values || values.length === 0) {
        return placeholder;
    }
    return '';
};

/**
 * LytMultiInput - A multi-value text input component for free-form entries like emails
 *
 * Similar to LytMultiSelect but without dropdown - allows creating custom values directly.
 *
 * @param {Object} props
 * @param {string} props.id - The id of the multi-input
 * @param {Array<string>} props.values - Array of current values
 * @param {Function} props.onChange - Callback when values change: (newValues) => void
 * @param {Function} props.validateValue - Optional validation function: (value) => boolean
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.validationErrorText - Error message to show on validation failure
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {string} props.delimiter - Character(s) that trigger adding a new value (default: ',')
 * @param {boolean} props.addOnBlur - Whether to add value on blur (default: true)
 */
const LytMultiInput = ({
    id = '',
    values = [],
    onChange = () => {},
    validateValue = null,
    placeholder = 'Type and press Enter',
    validationErrorText = 'Invalid input',
    disabled = false,
    delimiter = ',',
    addOnBlur = true,
}) => {
    const theme = useTheme();
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const [inputValue, setInputValue] = useState('');
    const [validationError, setValidationError] = useState('');
    const [isShaking, setIsShaking] = useState(false);
    // Track validation status per value: { value: boolean }
    const [validationStatus, setValidationStatus] = useState({});
    const triggerValidationError = useCallback((message) => {
        setValidationError(message);
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 400);
    }, []);
    const addValue = useCallback(
        (value) => {
            const trimmedValue = value.trim();
            if (!trimmedValue) return false;

            // Check for duplicates
            if (values.includes(trimmedValue)) {
                triggerValidationError('This value has already been added');
                return false;
            }

            // Validate if validator provided - but still add the value (just mark as invalid)
            const isValid = validateValue ? validateValue(trimmedValue) : true;

            // Update validation status for this value
            setValidationStatus((prev) => {
                const next = {
                    ...prev,
                    [trimmedValue]: isValid,
                };

                // Check if any tags (including the new one) are invalid
                const hasAnyInvalid = Object.values(next).some(
                    (valid) => valid === false
                );
                if (hasAnyInvalid) {
                    setValidationError(validationErrorText);
                } else {
                    setValidationError('');
                }
                return next;
            });
            onChange([...values, trimmedValue]);
            return true;
        },
        [
            values,
            onChange,
            validateValue,
            validationErrorText,
            triggerValidationError,
        ]
    );

    /**
     * Handles tag removal
     * @param {string} value - The value to remove
     */
    const handleTagRemove = useCallback(
        (value) => {
            const newValues = values.filter((v) => v !== value);
            onChange(newValues);
            // Clean up validation status for removed value and update error state
            setValidationStatus((prev) => {
                const next = {
                    ...prev,
                };
                delete next[value];

                // Check if any remaining tags are invalid
                const hasInvalidRemaining = Object.values(next).some(
                    (isValid) => isValid === false
                );
                if (!hasInvalidRemaining) {
                    setValidationError('');
                }
                return next;
            });
        },
        [values, onChange]
    );

    /**
     * Handles clearing all values
     */
    const handleClearAll = useCallback(
        (event) => {
            event.stopPropagation();
            onChange([]);
            setInputValue('');
            setValidationError('');
            setValidationStatus({});
        },
        [onChange]
    );

    /**
     * Handles the input container click event
     */
    const handleInputContainerClick = useCallback(() => {
        if (!disabled && inputRef.current) {
            inputRef.current.focus();
        }
    }, [disabled]);

    /**
     * Handles the input change event
     * Triggers tag creation on: comma+space (", "), space (" "), or the specified delimiter
     */
    const handleInputChange = useCallback(
        (event) => {
            const value = event.target.value;

            // Check for comma+space or space delimiter (higher priority)
            if (value.endsWith(', ') || value.endsWith(' ')) {
                // Remove the trailing delimiter and add the value
                const valueWithoutDelimiter = value.endsWith(', ')
                    ? value.slice(0, -2)
                    : value.slice(0, -1);
                if (valueWithoutDelimiter.trim()) {
                    addValue(valueWithoutDelimiter);
                }
                setInputValue('');
            } else if (delimiter && value.includes(delimiter)) {
                // Fall back to specified delimiter
                const parts = value.split(delimiter);
                // Add all parts except the last one (which might be incomplete)
                parts.slice(0, -1).forEach((part) => {
                    addValue(part);
                });
                // Keep the last part in the input
                setInputValue(parts[parts.length - 1]);
            } else {
                setInputValue(value);
            }
        },
        [delimiter, addValue]
    );

    /**
     * Handles keyboard events
     */
    const handleKeyDown = useCallback(
        (event) => {
            if (disabled) return;
            if (event.key === 'Enter') {
                event.preventDefault();
                if (inputValue.trim()) {
                    if (addValue(inputValue)) {
                        setInputValue('');
                    }
                }
            } else if (
                event.key === 'Backspace' &&
                inputValue === '' &&
                values.length > 0
            ) {
                // Remove the last value when backspace is pressed and input is empty
                const newValues = [...values];
                newValues.pop();
                onChange(newValues);
            } else if (event.key === 'Escape') {
                setInputValue('');
                setValidationError('');
            }
        },
        [disabled, inputValue, values, addValue, onChange]
    );

    /**
     * Handles blur event
     */
    const handleBlur = useCallback(() => {
        if (addOnBlur && inputValue.trim()) {
            if (addValue(inputValue)) {
                setInputValue('');
            }
        }
    }, [addOnBlur, inputValue, addValue]);
    const placeholderText = getPlaceholderText(values, placeholder);
    return (
        <MultiInputContainer ref={containerRef}>
            <InputContainer
                onClick={handleInputContainerClick}
                hasSelectedValues={values.length > 0}
                hasError={!!validationError}
                isShaking={isShaking}>
                <TagsContainer>
                    {values.map((value) => (
                        <LytTag
                            key={value}
                            onRemoveClick={() => handleTagRemove(value)}
                            ariaLabel={`Remove ${value}`}
                            variant={TAG_VARIANTS.DEFAULT}
                            appearance={TAG_APPEARANCES.DEFAULT}
                            isValid={validationStatus[value] !== false}>
                            {value}
                        </LytTag>
                    ))}
                    <HiddenInput
                        ref={inputRef}
                        id={id}
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        onBlur={handleBlur}
                        placeholder={placeholderText}
                        disabled={disabled}
                        aria-label={placeholder}
                        role="textbox"
                        autoComplete="off"
                    />
                </TagsContainer>
                {values.length > 0 && (
                    <IconsContainer>
                        <ClearButton
                            onClick={handleClearAll}
                            title="Clear all"
                            aria-label="Clear all">
                            <LytIcon
                                name={ICON_NAMES.X}
                                size={12}
                                color={
                                    theme.components.lyt_input.background
                                        .default
                                }
                            />
                        </ClearButton>
                    </IconsContainer>
                )}
            </InputContainer>

            {validationError && <ErrorMessage>{validationError}</ErrorMessage>}
        </MultiInputContainer>
    );
};
LytMultiInput.propTypes = {
    id: PropTypes.string,
    values: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func,
    validateValue: PropTypes.func,
    placeholder: PropTypes.string,
    validationErrorText: PropTypes.string,
    disabled: PropTypes.bool,
    delimiter: PropTypes.string,
    addOnBlur: PropTypes.bool,
};
export default LytMultiInput;
